import React, { useState, useEffect, useContext, useCallback } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { Menu, Search, BookOpen } from 'lucide-react';
import { AuthContext } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { API } from '../../lib/axiosClient';
import { CommandPalette } from '../CommandPalette';
import { NotificationBell } from '../NotificationBell';
import { UserManualPanel } from '../UserManualPanel';
import { TenantReadOnlyProvider } from '../../contexts/TenantReadOnlyContext';
import TenantReadOnlyBanner from '../layout/TenantReadOnlyBanner';
import { useSupplierClaimsBadge } from '../../hooks/useSupplierClaimsBadge';
import { useCostPostingsBadge } from '../../hooks/useCostPostingsBadge';
import { useDraftsBadge } from '../../hooks/useDraftsBadge';
import FioriTopNav from './FioriTopNav';
import FioriModulePanel from './FioriModulePanel';
import FioriMobileNav from './FioriMobileNav';
import FioriUserMenu from './FioriUserMenu';
import { buildGroups } from './fioriNav';
import '../../styles/fiori-shell.css';

// SAP-Fiori-Horizon-Enterprise-Shell: Top-Shellbar + horizontale Top-Nav.
// Ersetzt die Sidebar app-weit (Feature-Flag REACT_APP_FIORI_LAUNCHPAD).
export default function FioriShell() {
  const { user } = useContext(AuthContext);
  const { t } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [manualOpen, setManualOpen] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [activeModule, setActiveModule] = useState(null);
  const [unreadEmails, setUnreadEmails] = useState(0);

  // Mobile-Navigation + Modul-Panel bei Routenwechsel schliessen + Tab-Titel pflegen
  useEffect(() => {
    setMobileNavOpen(false);
    setActiveModule(null);
    document.title = 'QM8D.ai — Quality Intelligence';
  }, [location.pathname]);

  const { overdue: overdueQ2 } = useSupplierClaimsBadge();
  const { count: costPostingsPending } = useCostPostingsBadge();
  const { count: draftsPending } = useDraftsBadge();
  const badges = { unreadEmails, overdueQ2, costPostingsPending, draftsPending };

  // Aktive Top-Gruppe fuer das Modul-Panel (RBAC-gefilterte Items wie in der Nav)
  const groups = buildGroups(t, user);
  const activeGroup = activeModule ? groups.find((g) => g.id === activeModule) : null;

  // Cmd+K / Ctrl+K -> Command Palette · Escape -> Modul-Panel schliessen
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setPaletteOpen((p) => !p);
      }
      if (e.key === 'Escape') {
        setActiveModule(null);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const fetchUnread = useCallback(async () => {
    try {
      if (!user?.permissions?.can_manage_users) return;
      const res = await axios.get(`${API}/email-inbox/unread-count`, { timeout: 5000 });
      setUnreadEmails(res.data?.unread_count || 0);
    } catch { /* nicht kritisch */ }
  }, [user]);

  useEffect(() => {
    fetchUnread();
    const id = setInterval(fetchUnread, 30000);
    return () => clearInterval(id);
  }, [fetchUnread]);

  return (
    <TenantReadOnlyProvider>
      <div className="fiori-shell" data-testid="fiori-shell">
        <TenantReadOnlyBanner />

        {/* Shellbar */}
        <header className="fs-shellbar" role="banner" data-testid="fiori-shellbar">
          <button
            className="fs-icon-btn fs-burger"
            onClick={() => setMobileNavOpen(true)}
            aria-label={t('appLayout.openMenu', 'Menü öffnen')}
            data-testid="fiori-burger"
          >
            <Menu size={20} />
          </button>

          <button className="fs-brand" onClick={() => navigate('/home')} data-testid="fiori-brand">
            <span className="fs-brand-badge"><span>8D</span><span className="fs-brand-ai">AI</span></span>
            <span className="fs-brand-word">QM8D<b>.ai</b></span>
          </button>

          <button className="fs-search" onClick={() => setPaletteOpen(true)} data-testid="fiori-search" aria-label={t('common.searchPlaceholder', 'Suchen oder App starten')}>
            <Search size={14} />
            <span className="fs-search-hint">{t('fiori.searchPlaceholder', 'Suchen oder App starten')}</span>
            <kbd>Ctrl K</kbd>
          </button>

          <div className="fs-actions">
            <NotificationBell />
            <button className="fs-icon-btn" onClick={() => setManualOpen(true)} aria-label={t('nav.help', 'Hilfe')} data-testid="fiori-help">
              <BookOpen size={18} />
            </button>
            <FioriUserMenu />
          </div>
        </header>

        {/* Top-Navigation (Desktop) */}
        <FioriTopNav user={user} badges={badges} activeModule={activeModule} onSelectModule={setActiveModule} />

        {/* Modul-Panel (Kachel-Auswahl statt Dropdown) */}
        {activeGroup && (
          <FioriModulePanel
            group={activeGroup}
            badges={badges}
            onNavigate={(path) => { setActiveModule(null); navigate(path); }}
            onClose={() => setActiveModule(null)}
          />
        )}

        {/* Inhalt */}
        <main id="main-content" role="main" className="fiori-main">
          <Outlet />
        </main>

        {/* Mobile-Navigation */}
        <FioriMobileNav open={mobileNavOpen} onClose={() => setMobileNavOpen(false)} user={user} badges={badges} />

        <CommandPalette isOpen={paletteOpen} onClose={() => setPaletteOpen(false)} />
        <UserManualPanel isOpen={manualOpen} onClose={() => setManualOpen(false)} />
      </div>
    </TenantReadOnlyProvider>
  );
}
